import { DefaultComponentProps } from '../../components/app-router/component-props';
import { LogIn } from '../../components/login/login';
import { DailyLog } from '../../components/pages/daily-log/daily-log';
import { MainMenu } from '../../components/pages/main-menu';
import { VisitorLog } from '../../components/pages/visitor-log/visitor-log';
import { ResidentEntry } from '../../components/pages/visitor-log/resident-entry';
import { getDeliveryRoutes } from './delivery-routes';
import { getEmergencyRoutes } from './emergency-routes';
import { getGuestRoutes } from './guest-routes';
import { getLeasingOfficeRoutes } from './leasing-office-routes';
import { getMaintenanceRoutes } from './maintanance-routes';
import { getMisscelaneausRoutes } from './misscelaneaus-routes';
import { getProspectRoutes } from './prospect-routes';
import { getResidentRoutes } from './resident-routes';
import { getVendorRoutes } from './vendor-routes';
import { getShiftChangeRoutes } from './shift-change-routes';
import { Dashboard } from '../../components/pages/dashboard/dashboard';
import { ViewDailyReport } from '../../components/pages/view-daily-report/view-daily-report';
import {
  LegacyPortalRedirect,
  PortalShell,
} from '../../components/pages/portal/portal-shell';
import { RosterPage } from '../../components/pages/roster/roster-page';
import { ParcelDesk } from '../../components/pages/packages/parcel-desk';
import { VisitorExitPage } from '../../components/pages/visitor-exit/visitor-exit-page';
import { ActivityExitPage } from '../../components/pages/activity-exit/activity-exit-page';
import { ExpectedPage } from '../../components/pages/visitor-log/expected/expected-page';
import { DeskPassesPage } from '../../components/pages/visitor-log/passes/desk-passes-page';
import { IqHandoverPage } from '../../components/pages/iq/iq-handover-page';
import { ShiftMonitorPage } from '../../components/pages/shifts/shift-monitor-page';
import { AccountPage } from '../../components/pages/account/account-page';
import { ReceptionPage } from '../../components/pages/stays/reception/reception-page';
import { AdminConsole } from '../../components/pages/admin/admin-console';

export interface RouteType {
  path: string;
  component: React.FC<DefaultComponentProps>;
  options?: string[];
  /**
   * THIS ROUTE IS NOT A WIZARD STEP, it merely lives inside the wizard shell.
   *
   * app-router.tsx guards every non-entry route under <WizardLayout> with "has a
   * type been chosen yet?" — a deep link into step 4 of the guest flow with no
   * guest type would render a pane with nothing behind it. That guard reads
   * `visitorType` / `buildingActivity`, which only the PICKERS set.
   *
   * A one-screen route that is reached WITHOUT choosing a type therefore fails
   * the guard on every visit and bounces straight back to the picker. That is
   * exactly what happened to Resident in / out: its tile navigates directly (by
   * design — a resident has no five-step wizard to enter), so the screen was
   * unreachable. Marking it here is what tells the guard the route carries its
   * own context.
   */
  noWizardContext?: boolean;
}

/**
 * WIZARD STEP ROUTES — every step of the visitor-log / daily-log intake flow.
 *
 * These render INSIDE <WizardLayout> (see app-router.tsx), which owns the shell
 * (sidebar, step/progress, rail, card). Each step component therefore renders
 * ONLY its own content — never the shell — so moving between steps swaps just
 * the pane instead of remounting the whole page.
 */
export const wizardRoutes: RouteType[] = [
  // Type / activity pickers — step 1 of each wizard. They live INSIDE the
  // persistent shell so picker -> first step swaps only the pane (no full
  // shell remount on the first Continue).
  { path: '/visitor-log', component: VisitorLog },
  { path: '/daily-log', component: DailyLog },
  // ONE STEP, not a wizard. The desk already knows this person — they are on
  // the roster — so the entire interaction is "who" and "which direction".
  // Registered here beside the pickers so it renders inside the same persistent
  // shell and a Back returns to the type list without a full remount.
  //
  // noWizardContext: the tile that leads here deliberately does NOT go through
  // selectAndAdvance, so no visitorType is ever set — see visitor-log.tsx. The
  // shell's "did you pick a type" guard would otherwise redirect every visit
  // back to the picker, which is what made this screen unreachable.
  { path: '/visitor-log/resident', component: ResidentEntry, noWizardContext: true },
  // visitor-log
  ...getGuestRoutes(),
  ...getProspectRoutes(),
  ...getVendorRoutes(),
  ...getDeliveryRoutes(),
  ...getEmergencyRoutes(),
  // daily-log
  ...getMaintenanceRoutes(),
  ...getLeasingOfficeRoutes(),
  ...getResidentRoutes(),
  ...getMisscelaneausRoutes(),
  ...getShiftChangeRoutes(),
];

/**
 * STANDALONE ROUTES — full pages that render their own layout.
 */
export const routes: RouteType[] = [
  { path: '/', component: LogIn },
  { path: '/main-menu', component: MainMenu },
  { path: '/view-report', component: ViewDailyReport },
  // The resident roster, hosted OUTSIDE /dashboard so a concierge can reach it
  // from the main menu — /dashboard/* admits owners and property managers only.
  { path: '/roster', component: RosterPage },
  // The parcel desk, hosted OUTSIDE /dashboard for the same reason as /roster:
  // taking deliveries in and handing them over is the archetypal concierge
  // task, and /dashboard/* admits owners and property managers only.
  { path: '/parcels', component: ParcelDesk },
  // "Coming out" — the visitor exit desk, and the only caller of
  // POST /visitor-log/:id/exit. Hosted outside /dashboard for the same reason
  // as /roster and /parcels: it is front-desk work and /dashboard/* admits
  // owners and property managers only. Gated in ROUTE_PERMISSIONS on
  // `visitorlog:create`, mirroring the endpoint it exists to call rather than
  // picking a role list that happens to line up today.
  { path: '/visitor-exit', component: VisitorExitPage },
  // "Activities out" — the OUT half of the main menu's fourth pair, and the only
  // caller of POST /daily-log/:id/close (migration 079). Hosted beside
  // /visitor-exit for the same reasons and gated the same way: on
  // `dailylog:create` in ROUTE_PERMISSIONS, mirroring the endpoint rather than a
  // role list. Recording that an activity finished is the same class of act as
  // recording that it started.
  { path: '/daily-log-exit', component: ActivityExitPage },
  // "Expected today" — the desk's list of visitor passes for today and the one
  // tap that signs a visitor in on one (resident-portal Plan 2, RP-2.11).
  // STANDALONE, NOT a wizard route: the wizard map in app-router.tsx applies
  // only RequireRole(DESK_ROLES) and would skip ROUTE_PERMISSIONS, where this is
  // gated on `visitorpass:read` — what GET /buildings/:b/expected checks. It is
  // still a desk path (the /visitor-log prefix), so it sits in DeskShell and
  // gets the building context like every other desk screen.
  { path: '/visitor-log/expected', component: ExpectedPage },
  // "Visitor passes" at the desk — the pass register (every pass at the desk's
  // building, residents' included), reached from Expected today. Standalone for
  // the same reason as Expected today; gated on visitorpass:read, which is what
  // GET /buildings/:b/passes checks. Owner decision 2026-09-29.
  { path: '/visitor-log/passes', component: DeskPassesPage },
  // Lobbero IQ — the AI handover page, reachable from the main-menu tile.
  { path: '/iq-handover', component: IqHandoverPage },
  // The shift monitor. NOT an AI surface and NOT a role gate: it is gated on
  // `user:read` in ROUTE_PERMISSIONS (app-router.tsx), because the backend gates
  // it there too and a concierge is meant to get a hard 403 rather than an empty
  // list. Hosted outside /dashboard so it does not inherit that subtree's
  // owner/manager role gate on top of the permission one.
  { path: '/shift-monitor', component: ShiftMonitorPage },
  // THE RESIDENT PORTAL. WILDCARD, the same shape as '/account/*' below: the
  // shell owns its tabs (Home, Visitors, Parcels) as nested routes, so ONE entry
  // — and one key in ROUTE_PERMISSIONS — gates every portal path.
  { path: '/portal/*', component: PortalShell },
  // The portal's old address, kept FOREVER as a redirect: stored notification
  // rows (lobbero_be/src/notifications/registry.js) and push notifications
  // already on phones deep-link to it.
  { path: '/tenant-dashboard', component: LegacyPortalRedirect },
  // Your own account: details, password, devices, notifications. Deliberately
  // OUTSIDE /dashboard/* (owners and property managers only) and deliberately
  // absent from ROUTE_ROLES and ROUTE_PERMISSIONS, so the only gate is "signed
  // in" — a concierge and a resident must be able to correct their own name and
  // rotate their own password too.
  //
  // WILDCARD, because the page owns four sub-routes of its own. Each section
  // needs a URL so a notification, an email footer and the account menu can each
  // link to the ONE they mean; before this, every one of them landed on the
  // password form.
  { path: '/account/*', component: AccountPage },
  // A HOTEL'S RECEPTION (Independent Stays IS9): a staff screen on the
  // property's clock, outside /dashboard like the desk (plan IS9's one
  // exception to R-ISC.4). Gated on stays:desk in ROUTE_PERMISSIONS; an
  // organisation with Desk is sent to the main menu by the page (R-IS9.7).
  { path: '/reception', component: ReceptionPage },
  // ── THE INTERNAL OPERATIONS CONSOLE ──────────────────────────────────────
  //
  // WILDCARD, because the console owns twenty-six sections and two detail
  // routes of its own — a section needs a URL so an investigation can be pasted
  // into a ticket.
  //
  // DELIBERATELY ABSENT FROM ROUTE_ROLES AND ROUTE_PERMISSIONS (app-router.tsx).
  // Neither could express the gate: platform-admin membership is not a role in
  // `users.role` (it is a row in `platform_admins`) and it is not in the
  // permission matrix either — deliberately, so no product permission can ever
  // imply it. The console component checks `user.is_platform_admin` and
  // redirects anyone else, and the SERVER re-checks membership against the
  // database on every request behind it.
  { path: '/admin/*', component: AdminConsole },
  // DASHBOARD routes function differently then the others.
  // The Dashboard Component is loaded on 'dashboard/*',
  // and according to the url name after '/dashboard/',
  //  inside this component we load a different component with a switch statement (renderDashboardContentAccordingUrl) in dashboard.tsx file.
  { path: '/dashboard/*', component: Dashboard },
];
